import { DefinitionType } from "@common/utils/objectDefinitions";
import { t } from "../utils/translations/phrases";
import { translate } from "../utils/translations/translations";
import type { TranslationKeys } from "../utils/translations/typings";
import { InputManager } from "./inputManager";

/**
 * The "Trench Command" HUD (desktop): the stock HUD elements restyled in canvas and brass, with a round
 * brass-compass minimap, telegram-strip kill feed and a stitched health strap, plus the "Field
 * Instrument" weapon panel (name, big ammo count, magazine ticks, slot tabs) and gas alert.
 * Styles live in index.html under `body.trench-hud`; this module moves the ammo counter and the weapon
 * slots into the weapon panel and keeps the panel's header and magazine ticks up to date.
 */
class TrenchHudClass {
    active = false;
    private _lastKey = "";

    /**
     * How much to scale the HUD so it fits this screen: the bottom row (pouches, health strap, weapon
     * panel) needs about 1440 px of width and the tallest column about 820 px of height at scale 1.
     * Smaller screens scale it down (never below half); screens larger than 1080p scale it up a little
     * so it doesn't look tiny on 1440p and 4K.
     */
    fitScale(): number {
        if (!this.active) return 1;
        const width = window.innerWidth, height = window.innerHeight;
        const fit = Math.min(width / 1440, height / 820);
        const grow = Math.max(1, Math.min(width / 1920, height / 1080));
        return Math.max(0.5, Math.min(fit, grow, 1.5));
    }

    init(): void {
        if (this.active || InputManager.isMobile) return;
        const panel = document.getElementById("trench-weapon");
        const ammo = document.getElementById("weapon-ammo-container");
        const slots = document.getElementById("weapons-container");
        if (!panel || !ammo || !slots) return;
        this.active = true;
        document.body.classList.add("trench-hud");
        panel.querySelector(".tw-ammo")?.appendChild(ammo);
        panel.querySelector(".tw-tabs")?.appendChild(slots);
        // the armour readout sits in the armour row, left of the helmet/vest/backpack squares
        const armour = document.getElementById("th-armor");
        const equipment = document.getElementById("equipment-container");
        if (armour && equipment) equipment.prepend(armour);
        // re-run the UI scale and minimap layout now that the fit factor applies
        window.dispatchEvent(new Event("resize"));
    }

    /**
     * Armour: a level badge on each worn piece, and the total damage it blocks. Helmet and vest
     * reductions add up (server: amount *= 1 - (helmet + vest)), so a level 1 helmet and vest block 30%.
     */
    updateEquipment(equipment: Partial<Record<"helmet" | "vest" | "mask" | "backpack", { readonly level: number, readonly damageReduction?: number } | undefined>>): void {
        if (!this.active) return;
        let blocked = 0;
        for (const type of ["helmet", "vest", "mask", "backpack"] as const) {
            const slot = document.getElementById(`${type}-slot`);
            const def = equipment[type];
            if (!slot) continue;
            if (def && def.level > 0) slot.setAttribute("data-level", String(def.level));
            else slot.removeAttribute("data-level");
            if (type === "helmet" || type === "vest") blocked += def?.damageReduction ?? 0;
        }
        // a gas mask's share comes off what's left after the helmet and vest (as on the server)
        blocked = 1 - (1 - Math.min(1, blocked)) * (1 - (equipment.mask?.damageReduction ?? 0));
        const readout = document.getElementById("th-armor");
        if (readout) {
            const percent = Math.round(Math.min(1, blocked) * 100);
            readout.hidden = percent <= 0;
            const value = readout.querySelector(".v");
            if (value) value.textContent = `${percent}%`;
            readout.title = equipment.mask
                ? t("Your armour blocks <percent>% of all damage you take (and the gas mask keeps out 55% of gas)", { percent })
                : t("Your armour blocks <percent>% of all damage you take", { percent });
        }
    }

    private _caliberKey = "";

    /** the active gun's ammo beside the magazine count: a small cartridge (a shell for shotguns) in that ammo's colour, the calibre, and the reserve */
    updateCaliber(ammo: { readonly idString: string, readonly name: string, readonly characteristicColor: { readonly hue: number, readonly saturation: number, readonly lightness: number } } | undefined, reserve: number | string = 0): void {
        const key = ammo ? `${ammo.idString}|${reserve}` : "";
        if (key === this._caliberKey) return;
        this._caliberKey = key;
        const block = document.querySelector<HTMLElement>("#trench-weapon .tw-caliber");
        if (!block) return;
        block.hidden = !ammo;
        if (!ammo) return;
        const { hue, saturation, lightness } = ammo.characteristicColor;
        // readable on the dark panel: never washed out to white, never too dark
        const colour = `hsl(${hue} ${Math.min(95, saturation)}% ${Math.max(52, Math.min(66, lightness))}%)`;
        const shotgun = /gauge|12g/i.test(`${ammo.idString} ${ammo.name}`);
        block.querySelector(".twc-glyph")!.innerHTML = shotgun
            ? `<svg viewBox="0 0 9 20"><rect x="1" y="0.8" width="7" height="13" rx="1.4" fill="${colour}"/><path d="M2.4 2.6h4.2M2.4 4.4h4.2" stroke="rgba(0,0,0,.35)" stroke-width="0.8"/><rect x="0.6" y="13.2" width="7.8" height="6" rx="0.7" fill="#c49c48"/><path d="M0.6 17.6h7.8" stroke="#8d6e2c" stroke-width="0.9"/></svg>`
            : `<svg viewBox="0 0 9 20"><path d="M1.6 8.2Q1.6 2.2 4.5 0.4Q7.4 2.2 7.4 8.2Z" fill="${colour}"/><rect x="1.2" y="8" width="6.6" height="11.2" rx="0.8" fill="#c49c48"/><path d="M1.2 9.3h6.6" stroke="#8d6e2c" stroke-width="0.8"/><path d="M1.2 17.8h6.6" stroke="#8d6e2c" stroke-width="1"/><path d="M2.6 10.6V16.6" stroke="rgba(255,255,255,.35)" stroke-width="0.9"/></svg>`;
        // "5.45mm" -> "5.45 MM", "12 gauge" -> "12 GAUGE"
        // (its name in the player's language: the game's own words for it, else its definition's, 1.04.4)
        const ammoName = translate(ammo.idString as TranslationKeys);
        block.querySelector(".twc-name")!.textContent = ammoName.replace(/(\d)(mm)\b/i, "$1 $2");
        block.querySelector(".twc-count")!.textContent = String(reserve);
        block.classList.toggle("empty", reserve === 0);
        block.title = t("<ammo>: <count> in reserve", { ammo: ammoName, count: reserve });
    }

    updateWeapon(weapon: { readonly definition: { readonly idString: string, readonly name: string, readonly defType: DefinitionType, readonly capacity?: number, readonly fireMode?: unknown, readonly pour?: { readonly capacity: number }, readonly gravity?: true }, readonly count?: number } | undefined): void {
        if (!this.active) return;
        const panel = document.getElementById("trench-weapon");
        if (!panel) return;
        panel.classList.toggle("empty", !weapon);
        if (!weapon) return;
        const { definition } = weapon;
        const isGun = definition.defType === DefinitionType.Gun;
        // the Gas Can shows its fuel like a magazine: the big number and the ticks
        const pour = definition.defType === DefinitionType.Melee ? definition.pour : undefined;
        const capacity = isGun ? Math.max(1, definition.capacity ?? 1) : pour ? pour.capacity : 0;
        const count = weapon.count ?? 0;
        const key = `${definition.idString}|${count}|${capacity}`;
        if (key === this._lastKey) return;
        this._lastKey = key;

        const mode = isGun ? ([t("SINGLE"), t("BURST"), t("AUTO")][Number(definition.fireMode)] ?? "")
            : pour ? (count > 0 ? t("POUR") : t("EMPTY"))
                // the Gravity Gloves grab and throw rather than hit, and fire a plasma burst with nothing held
                : definition.defType === DefinitionType.Melee ? (definition.gravity ? t("GRAB · THROW · PLASMA") : t("MELEE")) : t("THROWABLE");
        const name = panel.querySelector<HTMLElement>(".tw-name");
        if (name) name.textContent = `${translate(((definition as { readonly translationString?: string }).translationString ?? definition.idString) as TranslationKeys).toUpperCase()}${mode ? ` \u00B7 ${mode}` : ""}`;
        const gun = panel.querySelector<HTMLImageElement>(".tw-gun");
        if (gun) {
            // bare hands get the drawn fist; everything else its own weapon picture
            const src = definition.idString === "fists" ? "./img/hud/fist.svg" : `./img/game/weapons/${definition.idString}.svg?v=${ASSET_STAMP}`;
            if (!gun.src.endsWith(src.slice(1))) { gun.style.visibility = "hidden"; gun.src = src; }
            gun.onload = () => { gun.style.visibility = "visible"; };
            gun.onerror = () => { gun.style.visibility = "hidden"; };
        }
        // magazine: one tick per round (grouped when the magazine is huge)
        const rounds = panel.querySelector<HTMLElement>(".tw-rounds");
        if (rounds) {
            rounds.style.display = isGun || pour ? "" : "none";
            if (isGun || pour) {
                const ticks = Math.min(capacity, 40);
                const full = Math.round((Math.min(count, capacity) / capacity) * ticks);
                if (rounds.childElementCount !== ticks) {
                    rounds.replaceChildren(...Array.from({ length: ticks }, () => document.createElement("i")));
                }
                rounds.querySelectorAll("i").forEach((tick, index) => tick.classList.toggle("spent", index >= full));
            }
        }
    }
}

export const TrenchHud = new TrenchHudClass();
